/**
 * VendorQuickBar - Barre de sélection rapide des vendeurs
 *
 * Affiche les vendeurs actifs comme de gros boutons cliquables
 * Le vendeur actif est mis en surbrillance avec un badge "Actif"
 * Changement IMMÉDIAT sans demande de code PIN (le temps est important sur le POS)
 *
 * Fonctionnalités:
 * - Changement rapide entre vendeurs (sans code PIN)
 * - Notification si session active ailleurs
 * - Mode hors ligne : switch local entre vendeurs (ventes en file d'attente)
 * - Boutons colorés visibles (bleu pour inactifs, vert pour actif)
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useVendorSession } from '../../contexts/VendorSessionContext';
import { useTenant } from '../../contexts/TenantContext';
import { api } from '../../services/api';
import type { Vendor } from '../../types/pos.types';

import { usePOSStore } from '../../hooks/usePOSStore';
import { logger } from '../../utils/logger';
import { getVendorColor, rememberVendorIds } from '../../utils/vendorColors';

export default function VendorQuickBar() {
  const { tenant } = useTenant();
  const {
    currentVendor,
    hasLock,
    isOnline,
    loading: sessionLoading,
    switchVendor,
  } = useVendorSession();

  // Utiliser vendorId depuis le store Zustand pour une source de vérité unique
  const vendorId = usePOSStore((state) => state.vendorId);

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(true);

  // Charger la liste des vendeurs (actifs, avec accès POS)
  useEffect(() => {
    if (!tenant) return;

    const loadVendors = async () => {
      try {
        const data = await api.listVendors(tenant.id);
        // Couleurs assises sur la liste complète (désactivés compris) — cf. vendorColors.ts
        rememberVendorIds(data.filter((v: Vendor) => v.role !== 'FIELD').map((v: Vendor) => v.id));
        // Exclure uniquement les FIELD-only (pas d'accès POS)
        // Les ADMIN avec POS coché apparaissent normalement
        const activeVendors = data.filter((v: Vendor) => v.active && v.role !== 'FIELD');
        setVendors(activeVendors);
        // Cacher les vendeurs en localStorage pour usage offline
        try {
          localStorage.setItem('pos_vendors_cache', JSON.stringify(activeVendors));
        } catch (e) {
          // localStorage plein — pas bloquant
        }
      } catch (error) {
        logger.error('Failed to load vendors:', error);
        // Offline : charger depuis le cache localStorage
        try {
          const cached = localStorage.getItem('pos_vendors_cache');
          if (cached) {
            setVendors(JSON.parse(cached));
          }
        } catch (e) {
          // Pas de cache disponible
        }
      } finally {
        setLoadingVendors(false);
      }
    };

    loadVendors();
  }, [tenant]);

  // Notification si lock perdu
  // Utiliser vendorId de Zustand au lieu de currentVendor pour éviter les problèmes de sync
  // Note: On n'affiche le warning QUE si une session existe (currentVendor != null)
  // car hasLock est false par défaut au démarrage
  useEffect(() => {
    if (vendorId && !hasLock && currentVendor) {
      // Afficher notification seulement si on a une session active
      logger.warn('⚠️ Session active sur un autre poste');
      // Optionnel: Toast notification
    }
  }, [vendorId, hasLock, currentVendor]);

  const handleVendorClick = async (clickedVendorId: string) => {
    // Si c'est le vendeur actuel ET qu'il a le lock → rien à faire
    if (vendorId === clickedVendorId && hasLock) {
      logger.log('[VendorQuickBar] Vendeur déjà connecté avec lock:', clickedVendorId);
      return;
    }

    // Si c'est le vendeur actuel MAIS sans lock ("Ailleurs") → reprise EXPLICITE (takeover):
    // l'opérateur réclame sa propre session prise sur un autre poste. Son panier suit.
    if (vendorId === clickedVendorId && !hasLock) {
      logger.log('[VendorQuickBar] Reprise explicite (takeover) de session pour:', clickedVendorId);
      handleSwitch(clickedVendorId, true);
      return;
    }

    // Mode offline : switchVendor gère désormais le switch local dégradé en interne
    // (panier sortant conservé, panier entrant restauré, lock serveur différé).
    if (!isOnline) {
      logger.log('[VendorQuickBar] 📴 Switch offline:', { from: vendorId, to: clickedVendorId });
      handleSwitch(clickedVendorId);
      return;
    }

    logger.log('[VendorQuickBar] Changement de vendeur:', { from: vendorId, to: clickedVendorId });
    // Changement IMMÉDIAT sans demander de PIN (le temps est important sur le POS)
    // Le PIN sert uniquement pour la connexion au Dashboard
    handleSwitch(clickedVendorId);
  };

  const handleSwitch = async (newVendorId: string, takeover = false) => {
    logger.log('[VendorQuickBar] handleSwitch appelé pour:', newVendorId, { takeover });
    // onLocked : le vendeur cible est actif sur un AUTRE poste (409).
    // REPRISE AUTOMATIQUE (depuis le 31/07/2026) : au marché on change de caisse en
    // permanence, une confirmation bloquante à chaque passage n'apportait que de la
    // friction. Elle n'apportait AUCUNE sécurité tant que les postes partageaient le
    // même identifiant ; maintenant qu'il est unique par machine, le verrou protège
    // réellement — le poste qui perd la main ne peut plus écraser le panier (409 sur
    // saveCart). Le seul refus qui subsiste est l'encaissement en cours côté backend
    // (anti double-vente), et il n'est pas contourné ici.
    await switchVendor(newVendorId, undefined, takeover, (workstation) => {
      if (takeover) {
        // 2e refus malgré la reprise = paiement en cours sur l'autre poste
        toast.error('Encaissement en cours sur un autre poste — reprise impossible', {
          duration: 6000,
        });
        return;
      }
      const wsLabel = workstation ? ` (poste ${workstation})` : '';
      toast.info(`Session reprise ici${wsLabel} — panier récupéré`, { duration: 4000 });
      handleSwitch(newVendorId, true); // reprise automatique
    });
  };

  // Attendre que le tenant soit chargé
  if (!tenant || loadingVendors) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" />
        Chargement des vendeurs...
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-yellow-700 bg-yellow-100 px-4 py-2 rounded-lg border-2 border-yellow-300">
        ⚠️ Aucun vendeur disponible (sauf ADMIN)
      </div>
    );
  }

  // Déterminer le nombre de colonnes selon le nombre de vendeurs
  const getGridColumns = () => {
    if (vendors.length === 1) return 'grid-cols-1';
    if (vendors.length === 2) return 'grid-cols-2';
    if (vendors.length === 3) return 'grid-cols-3';
    // 4 vendeurs ou plus : max 4 colonnes
    return 'grid-cols-2 lg:grid-cols-4';
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        {/* Vendor Buttons - Grille adaptative selon le nombre de vendeurs */}
        <div className={`grid ${getGridColumns()} gap-3`}>
          {vendors.map((vendor) => {
            // Utiliser vendorId du store Zustand au lieu de currentVendor
            const isActive = vendorId === vendor.id;
            const isDisabled = sessionLoading;
            // Couleur d'identité, stable et identique sur les 3 caisses
            const color = getVendorColor(vendor.id);

            return (
              <button
                key={vendor.id}
                onClick={() => handleVendorClick(vendor.id)}
                disabled={isDisabled}
                className={`
                  relative px-6 py-3 rounded-lg font-bold transition-all
                  shadow-lg w-full
                  ${
                    isActive
                      // Vendeur actif : fort contraste + taille + coche. La distinction ne
                      // repose PAS que sur la couleur (accessibilité — août 2026).
                      ? `${color.bg} text-white shadow-2xl ring-8 ${hasLock ? color.ring : 'ring-orange-400'} ring-offset-2 transform scale-105 py-5`
                      : 'bg-gray-100 text-gray-500 border-2 border-gray-300 hover:bg-gray-200 active:scale-95'
                  }
                  ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl hover:ring-2 hover:ring-blue-300'}
                `}
              >
                <div className="flex items-center justify-center gap-3">
                  {/* Coche franche sur l'actif : repère non coloré, lisible de loin */}
                  {isActive ? (
                    <div className="text-4xl">{hasLock ? '✓' : '⚠️'}</div>
                  ) : (
                    <div className={`w-6 h-6 rounded-full shrink-0 ${color.dot}`} />
                  )}

                  {/* Nom : très grand et en capitales quand le vendeur est actif */}
                  <span className={`text-center leading-tight ${
                    isActive
                      ? 'text-white text-3xl font-extrabold uppercase tracking-wide'
                      : 'text-gray-600 text-lg font-semibold'
                  }`}>
                    {vendor.name}
                  </span>

                  {/* Le gros repère dit déjà « actif » : ce badge ne sert plus qu'au cas Ailleurs */}
                  {isActive && !hasLock && (
                    <div className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-orange-400 text-orange-900">
                      Ailleurs
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Status Indicators - Compacts */}
        {/* Bannière hors-ligne UNIQUEMENT si un vendeur est connecté : sans vendeur (écran de
            sélection), il n'y a aucune vente possible ET aucun heartbeat pour gérer isOnline,
            donc navigator.onLine (peu fiable sur Safari/macOS au boot/réveil WiFi) pouvait
            laisser une bannière « hors-ligne » figée à tort. Avec un vendeur, le ping corrige
            isOnline en ~2 s, donc la bannière reflète bien l'état réel. */}
        {!isOnline && vendorId && (
          <div className="flex items-center gap-2 text-sm font-semibold text-orange-700 bg-orange-100 px-4 py-2 rounded-lg border-2 border-orange-300">
            📴 Mode hors ligne — les ventes sont enregistrées localement et seront synchronisées au retour du réseau
          </div>
        )}

        {/* Utiliser vendorId de Zustand au lieu de currentVendor pour éviter les problèmes de sync */}
        {/* Afficher seulement si session active (currentVendor != null) pour éviter faux positif au démarrage */}
        {vendorId && !hasLock && isOnline && currentVendor && (
          <div className="flex items-center gap-2 text-sm font-semibold text-orange-700 bg-orange-100 px-4 py-2 rounded-lg border-2 border-orange-300">
            ⚠️ Attention : Session active sur un autre poste
          </div>
        )}
      </div>
    </>
  );
}
